/**
 * Storage adapter with Firestore write-through cache.
 *
 * Strategy:
 * - `get()` reads from localStorage (synchronous, fast)
 * - `set()` writes to localStorage AND, for CLOUD keys, Firestore (async, fire-and-forget)
 * - `setLocal()` writes ONLY to localStorage — for high-frequency callers that manage
 *   their own (lower-frequency) cloud sync cadence separately (see useTimerStore.tick()).
 * - `initCloudStorage(uid)` seeds localStorage from Firestore on login
 * - Failed Firestore writes are queued and retried automatically
 *
 * This means stores don't need ANY changes — they still call storage.get/set
 * synchronously, and the cloud sync happens transparently in the background.
 *
 * ─── Sync Policy Gate ───
 * Every key that ever flows through storage.get/set/remove is classified in exactly
 * one of three ways (see the lists just below):
 *   - CLOUD:      synced to Firestore, one doc/collection shared by the whole ACCOUNT.
 *                 The key MUST be permitted by firestore.rules (singlesAllowed() for a
 *                 single doc, isDataCollection() for a collection) — enforced
 *                 mechanically by src/utils/__tests__/storage.syncPolicy.test.js, which
 *                 parses firestore.rules and diffs it against getSyncPolicy() below.
 *   - PER_DEVICE: synced to Firestore like CLOUD, but the doc is scoped to the DEVICE
 *                 that wrote it (see PER_DEVICE_SINGLE_DOC_KEYS below), for ephemeral
 *                 runtime state where "shared across every device on the account" is
 *                 itself the bug (a running countdown resuming on the wrong device).
 *                 MUST be permitted by firestore.rules' perDeviceSinglesAllowed() —
 *                 also enforced by storage.syncPolicy.test.js.
 *   - LOCAL_ONLY: deliberately device-local. storage.set()/remove() skip the
 *                 Firestore attempt ENTIRELY for these keys — no network call, no
 *                 pending-queue entry, no denial to silently swallow.
 * A key registered in none of the three is treated as LOCAL_ONLY with a loud console
 * warning (see _shouldSyncToCloud) rather than silently attempting — and failing —
 * a write firestore.rules was never told about. Add a new key to exactly one list.
 *
 * ─── Pending-write queue: account-scoped, not global ───
 * The retry queue for failed/offline Firestore writes lives at an ACCOUNT-scoped
 * localStorage key (_pendingQueueKey(), via the same getKey() every other key uses) and
 * every entry is stamped with the uid that queued it. Both are deliberate: a crash, a
 * force-quit, or a browser kill before a clean sign-out can leave entries queued, and the
 * NEXT account to sign in on this device must never have them flushed into ITS Firestore
 * path — one person's data landing in another person's account. The storage-key
 * partitioning is the primary guard (there is no shared location for two accounts'
 * queues to collide on); the per-entry uid stamp is a second, independent check in
 * _flushPendingWrites(). A queue written before this fix shipped (2026-08-30) carries no
 * uid at all — _discardLegacyPendingQueue() drops it unconditionally rather than
 * guessing which account it belonged to; see that function's docblock for why.
 */
import { doc, getDoc, setDoc, deleteDoc, collection, getDocs, writeBatch, serverTimestamp } from 'firebase/firestore';
// SetOptions equivalent for batch writes — merge:true preserves fields written by
// other clients (e.g. the native Android app) that aren't present in our payload.
const MERGE_OPTS = { merge: true };
import { db } from '../firebase/config';
import { runMigrations } from './migration';

const STORAGE_PREFIX = 'alphafocus_';

// ─── Pending-write queue key: account-scoped ───
// _pendingQueueKey() below reuses getKey() (defined further down) so the queue lives at
// alphafocus_{uid}__pending_writes — the SAME per-account partitioning as every other
// key, so there is no shared location for two accounts' queues to ever collide on. See
// the module docblock's "Pending-write queue" section for the full defect this fixes.
const PENDING_QUEUE_KEY_NAME = '_pending_writes';
// The exact bare literal this file used before per-account scoping shipped
// (2026-08-30) — kept as its own constant (not derived) so _discardLegacyPendingQueue()
// keeps finding it even if the scoping scheme above ever changes shape.
const LEGACY_PENDING_QUEUE_KEY = 'alphafocus__pending_writes';

// ─── Current user's UID (set by initCloudStorage) ───
let _uid = null;

// ─── Sync status tracking ───
let _syncStatus = 'synced'; // 'synced' | 'pending' | 'error'
let _syncing = false; // true during initial cloud pull — queues outgoing writes
const _subscribers = new Set();

// ─── Delta sync state ───
// Tracks what was last successfully synced to Firestore per collection key,
// so we can compute minimal diffs instead of rewriting every item.
const _lastSyncedState = new Map(); // key → Map<itemId, JSON string>

// ─── Timestamp of last successful pull per key ───
// Used to reconcile pending writes: skip stale queued writes that were
// enqueued before newer cloud data was pulled.
const _lastPulledAt = new Map(); // key → timestamp (ms)

function _setSyncStatus(status) {
    if (_syncStatus !== status) {
        _syncStatus = status;
        _subscribers.forEach(fn => fn(status));
    }
}

/**
 * Subscribe to sync status changes.
 * Returns an unsubscribe function.
 */
export function subscribeSyncStatus(callback) {
    _subscribers.add(callback);
    // Immediately fire with current status
    callback(_syncStatus);
    return () => _subscribers.delete(callback);
}

/** Get current sync status */
export function getSyncStatus() {
    return _syncStatus;
}

// ─── Collections that store arrays of items (fanned out to individual docs) ───
// CLOUD — must match firestore.rules' isDataCollection() exactly (see THE GATE above).
// ('ritual_log', singular, was removed 2026-08-30 — dead cruft nothing in src/ ever
// read or wrote; useRitualEngineStore uses 'ritual_logs', plural, for both. The
// identical dead name still needs removing from firestore.rules' isDataCollection()
// whitelist in the same change, or storage.syncPolicy.test.js's cross-check fails.)
const CLOUD_COLLECTION_KEYS = [
    'tasks', 'sessions', 'projects', 'identities',
    'areas', 'resources', 'reviews', 'flows', 'flow_templates',
    'properties', 'views', 'project_templates', 'quests',
    'ritual_definitions', 'ritual_logs', 'inbox'
];

const SORTABLE_COLLECTIONS = ['projects', 'resources', 'quests', 'flows', 'views', 'ritual_definitions'];

// ─── Single-document keys (stored as one Firestore doc each) ───
// CLOUD — must match firestore.rules' singlesAllowed() exactly (see THE GATE above).
const CLOUD_SINGLE_DOC_KEYS = [
    'active_identity',
    'flow_execution', 'flow_draft', 'user_preferences',
    'voice_dump_stats', 'voice_dump_templates', 'voice_dump_history',
    'ai_coach_conversations', 'migration_version',
    'sidebar_collapsed', 'projects_displayOptions', 'active_context',
    'command_palette_recent', 'view_defaults', 'onboarding_completed',
    'nav_groups',
    // ─── Added 2026-08-30 (sync-policy audit) — small preferences and vision-board
    // data that were silently device-local: registered here AND in firestore.rules'
    // singlesAllowed() in the same change. ───
    'alltasks_displayOptions', // AllTasks list display options (visibleProperties, etc.)
    'work_life_filter',        // Global Work/Life context toggle
    'calendar_settings',       // { connected, lastSyncAt, idealWeek } — the OAuth refresh
                                // token itself lives server-side in users/{uid}/integrations
                                // (write:false to clients), so this is genuinely per-ACCOUNT,
                                // not per-device: syncing it means Calendar stays connected
                                // on a second device without redoing the OAuth handshake.
    // Home v2 (2026-08-30): suggestion ids the user dismissed. Account-scoped on
    // purpose — "I don't want to be asked this again" is a decision about the
    // product, not about this laptop; re-nagging on a second device is the bug.
    'home_dismissed_suggestions',
    'vision_data',             // Vision board layout/elements. Can embed base64 image data
                                // URLs (Vision.jsx image upload) — see MAX_SINGLE_DOC_BYTES
                                // below for the 1 MiB Firestore document-size guard.
    'vision_canvas_settings',  // Vision board snap/background-theme prefs
];

// LOCAL_ONLY — deliberately never attempted against Firestore. Each one says why.
const LOCAL_ONLY_SINGLE_DOC_KEYS = [
    // Server-authoritative XP/coins/streak/achievements. Direct client writes are
    // forgeable, so firestore.rules' singlesAllowed() deliberately excludes this key
    // (see the rules file's comment above singlesAllowed()) — Firestore denies every
    // client write to it by design. The durable path is the awardEvent callable
    // (functions/src/gamification/awardEvent.js); see
    // useGamificationStore.syncSessionAward(). DO NOT move this to CLOUD_SINGLE_DOC_KEYS
    // — doing so would just generate writes Firestore rejects, same as before this fix.
    'gamification',
    // Re-fetchable cache of Google Calendar's own data (useCalendarStore.syncEvents
    // re-pulls the current month ±1 from the Calendar API on every sync) — not a
    // source of truth worth persisting, and can be large across many months/devices.
    'calendar_events',
    // BYOK secret (Settings > AI). Never leaves the device — this was already the
    // stated intent (see the removed comment this replaces) but was never actually
    // enforced: every key not in CLOUD_COLLECTION_KEYS fell through to a single-doc
    // Firestore write attempt regardless of this comment, so the key silently hit the
    // same "attempted, denied by rules, queued forever" failure as the six orphan
    // keys — see Defect A/C. This gate is what finally makes the exclusion real.
    'gemini_api_key',
];

// ─── Per-device single-document keys ───
// CLOUD, but the Firestore doc is scoped to the DEVICE that wrote it, not the account —
// see firestore.rules' perDeviceSinglesAllowed() (must match this list exactly; enforced
// by storage.syncPolicy.test.js the same way as the two lists above). The doc lives at
// _singles/{key}__{deviceId} (see _perDeviceDocId()/getDeviceId() below) instead of the
// bare _singles/{key} that CLOUD_SINGLE_DOC_KEYS uses. The LOCAL storage key is completely
// unaffected — still plain alphafocus_{uid}_{key} — so a same-device consumer that reads
// localStorage directly (the desktop mini-player's raw key scan + `storage` event
// listener in TauriMiniPlayerWindow.jsx) keeps working unmodified; only the Firestore
// mirror splits per device.
//
// 'timer_state': a running countdown belongs to the device physically running it. One
// doc per ACCOUNT meant a second device pulling it could resume — or silently overwrite —
// another device's live session (two open tabs, or a phone and a desktop, fighting over
// one countdown; a phone resuming a desktop's session). Per-device docs make that
// structurally impossible: a device only ever reads/writes its own doc.
//
// Audited alongside timer_state as the same "ephemeral runtime singleton" family (see
// docs/platform/roadmap.md) — 'flow_execution' and 'flow_draft' — and deliberately left
// in CLOUD_SINGLE_DOC_KEYS instead:
//   - flow_execution is fully RETIRED as of the circuit_run cutover (see useFlowStore.js's
//     file header + src/stores/__tests__/useFlowStore.cutover.test.js): nothing writes it
//     from the web client anymore. The cloud `_singles/circuit_run` doc (already
//     server-authoritative, its own write:false rule) is the live run state now. The only
//     remaining Firestore call against this key is the delete in useFlowStore's
//     completeStep()/stopExecution() — a deliberate cross-platform cleanup that clears a
//     stale PRE-cutover doc so Android's read-only legacy fallback
//     (FirestoreCircuitRepository.kt) can't get stuck showing a phantom "still running"
//     circuit. That cleanup must stay ACCOUNT-wide: splitting it per device would mean
//     only a device sharing this session's (newly-invented, unrelated) device id ever
//     clears the shared legacy doc, and every other device's read of it stays stale
//     forever. Nothing here needs — or would benefit from — per-device scoping.
//   - flow_draft is the Circuit Builder's in-progress, UNSAVED draft — a document being
//     composed, not a live running process. Unlike a countdown, picking the same draft
//     back up on a second device is the feature, not the bug. Last-write-wins between two
//     devices editing it at once is the same accepted characteristic every other
//     account-wide CLOUD single (user_preferences, active_context, ...) already has; it
//     is not the hijack failure mode this fix addresses.
const PER_DEVICE_SINGLE_DOC_KEYS = ['timer_state'];

// Back-compat aliases — the rest of this file (pull/push loops, sortable-collection
// checks, delta sync) reads exactly as before; only these two names' DEFINITIONS moved.
const COLLECTION_KEYS = CLOUD_COLLECTION_KEYS;
const SINGLE_DOC_KEYS = CLOUD_SINGLE_DOC_KEYS;

function _isCloudCollectionKey(key) {
    return CLOUD_COLLECTION_KEYS.includes(key);
}

function _isCloudSingleKey(key) {
    return CLOUD_SINGLE_DOC_KEYS.includes(key);
}

function _isLocalOnlyKey(key) {
    return LOCAL_ONLY_SINGLE_DOC_KEYS.includes(key);
}

function _isPerDeviceKey(key) {
    return PER_DEVICE_SINGLE_DOC_KEYS.includes(key);
}

/**
 * Whether storage.set()/remove() should even ATTEMPT a Firestore write for this key.
 * An unregistered key (present in NONE of the three lists) is treated as LOCAL_ONLY
 * defensively, with a loud dev warning, rather than silently attempting — and having
 * rules deny — a write nobody declared. This is the runtime half of THE GATE; the static
 * half is storage.syncPolicy.test.js, which fails at test time if this file and
 * firestore.rules ever disagree about which keys belong on the CLOUD/PER_DEVICE side.
 */
function _shouldSyncToCloud(key) {
    if (_isCloudCollectionKey(key) || _isCloudSingleKey(key) || _isPerDeviceKey(key)) return true;
    if (_isLocalOnlyKey(key)) return false;
    console.warn(
        `[Cloud sync] '${key}' is not registered in storage.js's sync policy (neither ` +
        `a CLOUD nor a LOCAL_ONLY key) — treating it as LOCAL_ONLY and skipping Firestore ` +
        `for it. Register it explicitly in CLOUD_SINGLE_DOC_KEYS/CLOUD_COLLECTION_KEYS ` +
        `(+ firestore.rules) or LOCAL_ONLY_SINGLE_DOC_KEYS near the top of storage.js.`
    );
    return false;
}

/**
 * Read-only snapshot of the sync policy. Consumed by
 * src/utils/__tests__/storage.syncPolicy.test.js to verify this file and
 * firestore.rules agree exactly; safe for any other code to introspect too.
 * Returns fresh arrays (copies), never the live internal ones.
 */
export function getSyncPolicy() {
    return {
        cloudSingleDocKeys: [...CLOUD_SINGLE_DOC_KEYS],
        cloudCollectionKeys: [...CLOUD_COLLECTION_KEYS],
        localOnlySingleDocKeys: [...LOCAL_ONLY_SINGLE_DOC_KEYS],
        perDeviceSingleDocKeys: [...PER_DEVICE_SINGLE_DOC_KEYS],
    };
}

// ─── Per-device identity ───
const DEVICE_ID_KEY = 'alphafocus__device_id';

/**
 * Stable identifier for THIS browser/desktop installation. Deliberately NOT tied to any
 * account (not read through getKey()'s uid-scoping, and not cleared by
 * clearCloudStorage()) — a physical device doesn't change identity when the account
 * signed into it does, so a second account signing in on the same machine reuses the
 * same device id, and per-device docs for account A vs. account B on this device simply
 * live under different {uid} paths with a shared deviceId suffix. Generated once via
 * crypto.randomUUID() (AGENTS.md's ID convention) and persisted to a flat localStorage
 * key.
 *
 * Reads-and-creates-if-absent on every call rather than caching in a module variable:
 * localStorage IS the only source of truth, so the id (a) survives a reload (the same
 * value comes back) and (b) never multiplies (the presence check always finds what an
 * earlier call already created) — and it stays correct across a `vi.resetModules()`
 * style reload in tests, where a module-level cache would silently go stale.
 */
// In-memory fallback used ONLY when localStorage itself is unavailable (getDeviceId's
// catch path below). Generated at most once per page load and reused for every
// subsequent call in that session — without this, a broken-storage environment would
// mint a FRESH random id on every single call, meaning every write in the same session
// would target a different Firestore document, silently multiplying per-device docs
// and defeating the whole point of per-device scoping.
let _fallbackDeviceId = null;

export function getDeviceId() {
    try {
        let id = localStorage.getItem(DEVICE_ID_KEY);
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem(DEVICE_ID_KEY, id);
        }
        return id;
    } catch {
        // localStorage unavailable — degrade to a fallback id that stays STABLE for the
        // rest of this page's lifetime (see _fallbackDeviceId above), not a fresh one
        // per call. This can never be WORSE than the bug being fixed (one doc shared
        // account-wide): at worst this device fails to recognise its own past doc and
        // starts a new one; it never reads or clobbers another device's.
        if (!_fallbackDeviceId) _fallbackDeviceId = crypto.randomUUID();
        return _fallbackDeviceId;
    }
}

/**
 * Firestore doc id for a PER_DEVICE_SINGLE_DOC_KEYS key — must match firestore.rules'
 * perDeviceSinglesAllowed() shape (`<baseKey>__<deviceId>`) exactly.
 */
function _perDeviceDocId(key) {
    return `${key}__${getDeviceId()}`;
}

/**
 * Resolves the actual _singles/{docId} id to use for a logical key: plain for an
 * account-wide CLOUD single, device-suffixed for a PER_DEVICE_SINGLE_DOC_KEYS one.
 */
function _singleDocId(key) {
    return _isPerDeviceKey(key) ? _perDeviceDocId(key) : key;
}

// ─── Firestore per-document size guard ───
// Firestore's hard limit is 1,048,576 bytes/doc. That applies to EVERY document this
// file ever writes, not just the CLOUD_SINGLE_DOC_KEYS branch: a single-doc value
// (vision_data can embed base64 image data URLs from Vision.jsx's image upload) is one
// document, and so is each individual item inside a CLOUD_COLLECTION_KEYS fan-out
// (inbox items can carry a voice-capture attachment as a base64 blob — see
// docs/data-schemas.md's Inbox Item shape). _checkDocSize() below is the one place
// both branches of _syncToFirestoreInternal check a payload against this ceiling
// BEFORE attempting the write — cheaper than burning a round trip only to have
// Firestore reject it, and it lets "too big" be treated as a permanent (don't
// retry-forever) condition like a rules denial, rather than a transient network
// failure.
//
// The two call sites differ only in what "too big" means for their write shape: a
// single doc ABORTS the write (there was nothing else batched alongside it anyway),
// while one oversized item inside a collection fan-out is EXCLUDED from just that
// item's own op, so every OTHER item in the same write still commits. That distinction
// matters because Firestore batches are all-or-nothing — if an oversized item's
// payload ever reached batch.set(), its rejection would fail the entire chunk, and if
// the resulting error were then treated as a permanent failure for the whole KEY (as
// the single-doc branch treats its own), it would silently wedge that key's ENTIRE
// collection sync — every other item along with it — until the offending item was
// edited back under the limit.
const MAX_SINGLE_DOC_BYTES = 900000; // ~148 KiB of headroom under the 1 MiB doc limit

function _byteLength(str) {
    try {
        return new TextEncoder().encode(str).length;
    } catch {
        return str.length; // conservative-enough fallback if TextEncoder is unavailable
    }
}

/**
 * Serializes `value` and checks it against MAX_SINGLE_DOC_BYTES. Never throws — each
 * call site decides what "oversized" means for its own write shape (see the guard
 * comment above). Centralizing the size math here means a future third write path
 * gets the same guard for free just by calling this before it writes anything, instead
 * of a new call site having to remember to reimplement the check.
 */
function _checkDocSize(value) {
    const json = JSON.stringify(value);
    const size = json ? _byteLength(json) : 0;
    return { json, size, oversized: size > MAX_SINGLE_DOC_BYTES };
}

/**
 * Whether a Firestore write failure is PERMANENT for the given payload — i.e.
 * retrying with the same key/value/auth will never succeed, so the entry should be
 * dropped (with a warning) rather than requeued forever. Distinguishes this from
 * genuinely transient failures (offline, timeout, server hiccup), which SHOULD
 * keep retrying.
 */
function _isPermanentSyncError(err) {
    if (!err) return false;
    if (err.permanent) return true; // our own synthetic markers (e.g. oversized doc)
    return err.code === 'permission-denied' || err.code === 'invalid-argument';
}

// ─── Pending writes queue ───
// Account-scoped: see the module docblock's "Pending-write queue" section. getKey() is
// defined further below in this file; _pendingQueueKey() is only ever CALLED (not
// defined-and-run) after module init completes, so the forward reference is safe.
function _pendingQueueKey() {
    return getKey(PENDING_QUEUE_KEY_NAME);
}

function _getPendingQueue() {
    try {
        const raw = localStorage.getItem(_pendingQueueKey());
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function _savePendingQueue(queue) {
    try {
        localStorage.setItem(_pendingQueueKey(), JSON.stringify(queue));
    } catch (e) {
        console.error('[Cloud sync] Could not save pending queue:', e);
    }
}

function _addPendingWrite(key, value) {
    const queue = _getPendingQueue();
    // Replace existing entry for the same key to avoid stale data
    const filtered = queue.filter(entry => entry.key !== key);
    // uid is stamped so _flushPendingWrites() can independently verify an entry belongs
    // to the account it's about to be flushed under — belt-and-suspenders alongside the
    // storage-key partitioning above (see the module docblock).
    filtered.push({ key, value, queuedAt: Date.now(), uid: _uid });
    _savePendingQueue(filtered);
    _setSyncStatus('pending');
}

/**
 * One-time (per browser-storage-lifetime) discard of the pre-2026-08-30 GLOBAL pending
 * queue (alphafocus__pending_writes, unscoped by account). Those entries carry no
 * account attribution — there is no uid field to check, and no reliable way to infer one
 * — so the only safe action is to drop them outright; assuming they belong to whichever
 * account is signing in now is exactly the cross-account leak this fix closes, even in
 * the common case where that guess would happen to be right. Each entry's LOCAL copy of
 * its data is untouched (storage.set()'s _writeLocal() always runs before anything is
 * ever queued) — only the queued CLOUD retry is dropped. A future edit to the same key
 * queues and syncs normally under whichever account makes it. Idempotent: a no-op once
 * the legacy key is gone, so it's safe to call unconditionally on every boot.
 */
function _discardLegacyPendingQueue() {
    try {
        const raw = localStorage.getItem(LEGACY_PENDING_QUEUE_KEY);
        if (raw === null) return;
        localStorage.removeItem(LEGACY_PENDING_QUEUE_KEY);
        let entries;
        try { entries = JSON.parse(raw); } catch { entries = null; }
        const count = Array.isArray(entries) ? entries.length : 0;
        if (count > 0) {
            console.warn(
                `[Cloud sync] Discarded ${count} pending write(s) queued before per-account ` +
                `queue scoping shipped (2026-08-30) — they carry no account attribution, so ` +
                `flushing them under whichever account is signing in now could write one ` +
                `person's data into another person's Firestore. Nothing else was touched: ` +
                `each write's local copy is intact, and a future edit to the same key will ` +
                `sync normally under the correct account.`
            );
        }
    } catch (e) {
        console.warn('[Cloud sync] Could not inspect/discard the legacy pending queue:', e.message);
    }
}

function _removePendingWrite(key) {
    const queue = _getPendingQueue();
    const filtered = queue.filter(entry => entry.key !== key);
    _savePendingQueue(filtered);
    if (filtered.length === 0) {
        _setSyncStatus('synced');
    }
}

/**
 * Flush all pending writes. Called after successful writes and on init.
 * Reconciles queued timestamps against pull timestamps to prevent stale
 * offline data from overwriting fresher cloud data.
 *
 * Defect-A fix: a single permanently-denied entry must NEVER block the ones behind
 * it. We isolate each entry's failure — permanent failures (rules denial, oversized
 * doc, bad payload) are dropped with a warning and the loop CONTINUES; transient
 * failures (offline, timeout) stay queued for the next flush, and the loop still
 * CONTINUES past them. Only the pending-queue's overall status reflects whether
 * anything is still outstanding at the end.
 */
async function _flushPendingWrites() {
    if (!_uid) return;

    const queue = _getPendingQueue();
    if (queue.length === 0) {
        _setSyncStatus('synced');
        return;
    }

    _setSyncStatus('pending');
    let anyTransientFailure = false;

    for (const entry of queue) {
        // Belt-and-suspenders account check: the queue is already partitioned by
        // account at the STORAGE KEY level (_pendingQueueKey() is uid-scoped), so this
        // branch should be unreachable in practice — but an entry whose stamped uid
        // disagrees with the account we are CURRENTLY flushing under must never be
        // written to that account's Firestore path regardless. This is the second,
        // independent guard against the cross-account leak; never remove it just
        // because the partitioning above makes it look redundant.
        if (entry.uid && entry.uid !== _uid) {
            console.warn(`[Cloud sync] Dropping pending write for '${entry.key}' — queued under a different account than the one currently signed in.`);
            _removePendingWrite(entry.key);
            continue;
        }

        // Task 4.5: Skip writes that were queued before the last cloud pull
        // for this key — the pulled data is newer and should take precedence.
        const pulledAt = _lastPulledAt.get(entry.key);
        if (pulledAt && entry.queuedAt && entry.queuedAt < pulledAt) {
            console.log(`[Cloud sync] Skipping stale pending write for '${entry.key}' (queued ${entry.queuedAt} < pulled ${pulledAt})`);
            _removePendingWrite(entry.key);
            continue;
        }

        // Sync-policy gate: a LOCAL_ONLY key should never have been queued in the
        // first place (storage.set()/remove() gate this now), but an entry queued by
        // a PRE-GATE session (e.g. a stale 'gamification' write sitting in a user's
        // browser from before this fix shipped) must be dropped, not attempted.
        if (!_shouldSyncToCloud(entry.key)) {
            console.warn(`[Cloud sync] Dropping stale queued write for '${entry.key}' — it is a LOCAL_ONLY key and is never synced.`);
            _removePendingWrite(entry.key);
            continue;
        }

        try {
            await _syncToFirestoreInternal(entry.key, entry.value);
            _removePendingWrite(entry.key);
        } catch (err) {
            if (_isPermanentSyncError(err)) {
                console.warn(`[Cloud sync] Dropping '${entry.key}' — permanently rejected (${err.code || 'oversized-doc'}): ${err.message}. Will not retry.`);
                _removePendingWrite(entry.key);
                continue;
            }
            console.warn('[Cloud sync] Retry failed for', entry.key, err.message);
            anyTransientFailure = true;
            // Keep queued for the next flush — but DO NOT stop the loop. Every other
            // entry still gets its own chance this pass (the fix for Defect A).
        }
    }

    _setSyncStatus(anyTransientFailure ? 'error' : 'synced');
}

// ─── localStorage adapter (always available) ───
const getKey = (key) => _uid ? `${STORAGE_PREFIX}${_uid}_${key}` : `${STORAGE_PREFIX}${key}`;

/** Shared local-write logic for storage.set() and storage.setLocal(). */
function _writeLocal(key, value) {
    let valueToSave = value;
    if (SORTABLE_COLLECTIONS.includes(key) && Array.isArray(value)) {
        valueToSave = value.map((item, index) => ({
            ...item,
            sortOrder: index
        }));
    }

    try {
        localStorage.setItem(getKey(key), JSON.stringify(valueToSave));
    } catch (e) {
        console.error('Storage write failed:', e);
    }

    return valueToSave;
}

export const storage = {
    get(key, defaultValue = null) {
        try {
            const raw = localStorage.getItem(getKey(key));
            return raw ? JSON.parse(raw) : defaultValue;
        } catch {
            return defaultValue;
        }
    },

    set(key, value) {
        const valueToSave = _writeLocal(key, value);

        // Fire-and-forget Firestore write with retry queue — only for CLOUD keys.
        if (_uid && _shouldSyncToCloud(key)) {
            if (_syncing) {
                // Task 4.4: Queue writes during cloud pull instead of dropping them.
                // They will be flushed (with reconciliation) after the pull completes.
                _addPendingWrite(key, valueToSave);
            } else {
                syncToFirestore(key, valueToSave);
            }
        }
    },

    /**
     * localStorage-only write — deliberately skips Firestore even for a CLOUD key.
     * For high-frequency callers that need their cloud copy updated at a much lower
     * cadence than their local copy (e.g. useTimerStore.tick() persists locally every
     * second so a refresh restores to the second, but only syncs to Firestore on
     * meaningful transitions via storage.set() — previously it synced to Firestore on
     * every tick too, ~600 writes to one doc per 50-minute session).
     *
     * This is a per-CALL choice, orthogonal to the per-KEY CLOUD/LOCAL_ONLY policy
     * above: the key itself is still CLOUD (other callers/transitions for the same
     * key should keep using storage.set() to actually reach Firestore) — this call
     * just opts out of syncing THIS particular write.
     */
    setLocal(key, value) {
        _writeLocal(key, value);
    },

    remove(key) {
        localStorage.removeItem(getKey(key));

        if (_uid && _shouldSyncToCloud(key)) {
            removeFromFirestore(key).catch(err =>
                console.warn('[Cloud sync] Remove failed:', key, err.message)
            );
        }
    },

    clear() {
        const prefix = _uid ? `${STORAGE_PREFIX}${_uid}_` : STORAGE_PREFIX;
        Object.keys(localStorage)
            .filter(k => k.startsWith(prefix))
            .forEach(k => localStorage.removeItem(k));
    }
};

export function clearCloudStorage() {
    // Purge pending writes to prevent cross-account data leakage. Compute the
    // OUTGOING account's queue key BEFORE _uid is cleared below — once _uid is null,
    // _pendingQueueKey() can no longer resolve back to this account's queue.
    try {
        if (_uid) localStorage.removeItem(_pendingQueueKey());
        // Defensive: also clear the pre-fix legacy global queue if one somehow still
        // exists (e.g. a session that signed out before ever calling
        // initCloudStorage(), which is where _discardLegacyPendingQueue() normally runs).
        localStorage.removeItem(LEGACY_PENDING_QUEUE_KEY);
    } catch (e) {
        console.warn('[Cloud sync] Could not clear pending writes on sign-out:', e);
    }
    _lastSyncedState.clear();
    _lastPulledAt.clear();
    _uid = null;
    _setSyncStatus('synced');
}

/**
 * Lightweight UID setter — makes storage.get/set use the correct user-scoped keys
 * WITHOUT triggering any Firestore pull or rehydration.
 * Used by the mini-player window which bypasses AuthGate.
 */
export function setStorageUid(uid) {
    _uid = uid;
}

// ─── Store rehydration ───
// Stores register a callback that re-reads their state from localStorage.
// Called after initCloudStorage so stores pick up the cloud-pulled data.
const _rehydrateCallbacks = new Set();

/**
 * Register a callback to re-read store state from localStorage
 * after cloud sync completes. Called once after initCloudStorage.
 */
export function onRehydrate(callback) {
    _rehydrateCallbacks.add(callback);
    return () => _rehydrateCallbacks.delete(callback);
}

function _fireRehydrate() {
    _rehydrateCallbacks.forEach(fn => fn());
}

// ─── Firestore sync helpers ───

/**
 * Public entry point: attempts Firestore write, queues on failure.
 */
async function syncToFirestore(key, value) {
    try {
        _setSyncStatus('pending');
        await _syncToFirestoreInternal(key, value);
        _removePendingWrite(key);
        // Try to flush any other pending writes while we're connected
        await _flushPendingWrites();
    } catch (err) {
        if (_isPermanentSyncError(err)) {
            // Permanently rejected (rules denial, oversized doc, bad payload) —
            // retrying would just fail again. Drop it instead of queuing it, so it
            // can never wedge the flush loop for every other key (Defect A).
            console.warn(`[Cloud sync] '${key}' was permanently rejected (${err.code || 'oversized-doc'}) — not queuing for retry:`, err.message);
            _removePendingWrite(key); // clears any stale entry for this key too
            const queue = _getPendingQueue();
            _setSyncStatus(queue.length === 0 ? 'synced' : 'error');
            return;
        }
        console.warn('[Cloud sync] Write failed, queuing for retry:', key, err.message);
        _addPendingWrite(key, value);
    }
}

/**
 * Internal Firestore write logic (no retry/queue logic here).
 *
 * Task 4.2 — Delta sync for collections:
 * Instead of batch.set() for EVERY item, we compare against _lastSyncedState
 * to only write changed/new items and delete removed items.
 * Includes batch chunking to stay under Firestore's 500-operation limit.
 */
async function _syncToFirestoreInternal(key, value) {
    if (!_uid) return;

    // Safety net: should be unreachable, since storage.set()/remove() and
    // _flushPendingWrites() all gate on _shouldSyncToCloud() before ever calling this
    // function — kept here too so no future caller can bypass the sync policy.
    if (!_shouldSyncToCloud(key)) return;

    if (COLLECTION_KEYS.includes(key) && Array.isArray(value)) {
        const colRef = collection(db, 'users', _uid, key);

        // Build current state map: id → JSON string. An item over the size guard is
        // EXCLUDED here — never batched, never retried as part of this key's write —
        // rather than allowed to reach batch.set()/commit(), which would reject the
        // WHOLE chunk it landed in (see the MAX_SINGLE_DOC_BYTES comment above).
        // Recorded in oversizedIds so the "deleted items" pass below can tell "genuinely
        // removed locally" apart from "still here, just too big to sync right now" — an
        // item that WAS already synced and later grew past the limit (e.g. a voice
        // attachment added to an existing inbox note) must keep its last-good cloud
        // copy, not have it deleted.
        const currentState = new Map();
        const oversizedIds = new Set();
        value.forEach(item => {
            const itemId = item.id || crypto.randomUUID();
            const { json, size, oversized } = _checkDocSize(item);
            if (oversized) {
                oversizedIds.add(itemId);
                console.warn(
                    `[Cloud sync] Skipping '${key}/${itemId}' — ${size} bytes, over the ` +
                    `${MAX_SINGLE_DOC_BYTES}-byte safety margin under Firestore's 1 MiB ` +
                    `document limit. The local copy is unaffected and the rest of '${key}' ` +
                    `still syncs; this item will sync once it's edited back under the ` +
                    `limit (e.g. removing a large voice/image attachment).`
                );
                return;
            }
            currentState.set(itemId, json);
        });

        // Get previous synced state (or empty)
        const prevState = _lastSyncedState.get(key) || new Map();

        // Compute delta operations
        const ops = []; // { type: 'set'|'delete', id, data? }

        // New or changed items
        for (const [id, json] of currentState) {
            if (!prevState.has(id) || prevState.get(id) !== json) {
                ops.push({ type: 'set', id, data: value.find(i => i.id === id) || JSON.parse(json) });
            }
        }

        // Deleted items (were in prevState but not in current) — but never for an id
        // that's merely oversized right now (see the comment above currentState): it
        // still exists locally, it just didn't make it into this write.
        for (const id of prevState.keys()) {
            if (!currentState.has(id) && !oversizedIds.has(id)) {
                ops.push({ type: 'delete', id });
            }
        }

        // Chunk operations into batches of 499 (leaving 1 slot margin)
        const BATCH_LIMIT = 499;
        for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
            const chunk = ops.slice(i, i + BATCH_LIMIT);
            const batch = writeBatch(db);

            for (const op of chunk) {
                if (op.type === 'set') {
                    // Field-preserving merge. Stamp updatedAt on every write and
                    // createdAt only when this id had no prior synced state (new doc),
                    // so existing createdAt (immutable per rules) is never overwritten.
                    // merge:true so a field-level write from another client (native
                    // Android) isn't clobbered by our partial item payload.
                    const isNew = !prevState.has(op.id);
                    const payload = {
                        ...op.data,
                        updatedAt: serverTimestamp(),
                        ...(isNew && !op.data.createdAt ? { createdAt: serverTimestamp() } : {}),
                    };
                    batch.set(doc(colRef, op.id), payload, MERGE_OPTS);
                } else if (op.type === 'delete') {
                    batch.delete(doc(colRef, op.id));
                }
            }

            await batch.commit();
        }

        // Refresh last synced state from the ACTUAL cloud collection after the
        // commit. Re-reading (instead of trusting our own `currentState`) keeps the
        // delta baseline aligned with documents written by other clients (native
        // Android) during this long-lived session, so a later web write does not
        // compute a spurious delete for a doc we simply never had locally.
        await _refreshSyncedState(key, colRef, currentState);
    } else {
        // Single document. Guard against Firestore's 1 MiB doc limit BEFORE
        // attempting the write (vision_data can embed base64 image data URLs) —
        // cheaper than a wasted round trip, and lets the caller treat this the same
        // way as a permanent rules denial (drop, don't retry-forever) rather than a
        // transient network failure.
        const { size, oversized } = _checkDocSize(value);
        if (oversized) {
            const err = new Error(
                `'${key}' is ${size} bytes — over the ${MAX_SINGLE_DOC_BYTES}-byte safety ` +
                `margin under Firestore's 1 MiB document limit. Skipping this Firestore ` +
                `write; the local copy is unaffected. It will be retried automatically the ` +
                `next time this key is saved (e.g. after removing some content).`
            );
            err.permanent = true;
            throw err;
        }

        // _singleDocId() routes PER_DEVICE_SINGLE_DOC_KEYS to their device-suffixed doc;
        // every other single doc keeps its plain key as before.
        const docRef = doc(db, 'users', _uid, '_singles', _singleDocId(key));
        // serverTimestamp() so updatedAt is authoritative across web + native clients
        // (roadmap Phase 1). value stays as-is; merge not needed for the singles wrapper.
        await setDoc(docRef, { value, updatedAt: serverTimestamp() });
    }
}

/**
 * Re-read a collection from Firestore and store its true state as the delta
 * baseline (_lastSyncedState). Called after each successful collection sync so the
 * baseline reflects writes from other clients (native Android) made during this
 * session — preventing the clobber window from widening over a long-lived tab.
 * On read failure, falls back to the locally-computed state so we never block.
 */
async function _refreshSyncedState(key, colRef, fallbackState) {
    try {
        const snapshot = await getDocs(colRef);
        const stateMap = new Map();
        snapshot.docs.forEach(d => {
            const data = d.data();
            const id = data.id || d.id;
            if (id) stateMap.set(id, JSON.stringify(data));
        });
        _lastSyncedState.set(key, stateMap);
        _lastPulledAt.set(key, Date.now());
    } catch (err) {
        console.warn(`[Cloud sync] Could not refresh synced state for '${key}':`, err.message);
        _lastSyncedState.set(key, fallbackState);
    }
}

async function removeFromFirestore(key) {
    if (!_uid) return;
    const docRef = doc(db, 'users', _uid, '_singles', _singleDocId(key));
    await deleteDoc(docRef);
}

// ─── Cloud initialization (called by AuthGate after login) ───

/** Max time to wait for Firestore on a cold boot before falling back to local data */
const BOOT_TIMEOUT_MS = 8000;

/**
 * Seeds localStorage from Firestore. Called once after authentication.
 * If user has cloud data, it overwrites local. If not, migrates local → cloud.
 * Also flushes any pending writes from previous sessions.
 *
 * Resilient startup: if Firestore is unreachable (e.g. app launched on Windows
 * startup before network is ready), gracefully falls back to local cache and
 * retries cloud sync in the background.
 */
export async function initCloudStorage(uid) {
    // Discard any pre-fix, unattributable global queue BEFORE this (or any) account's
    // own flush runs — see _discardLegacyPendingQueue()'s docblock.
    _discardLegacyPendingQueue();
    _uid = uid;
    _syncing = true; // Guard: block outgoing writes during pull

    // Fast-boot check: Does the user already have some local data seeded?
    const hasLocalData = !!localStorage.getItem(`${STORAGE_PREFIX}${uid}_identities`) || !!localStorage.getItem(`${STORAGE_PREFIX}${uid}_tasks`);

    const bootSequence = async () => {
        try {
            // Check if user has any cloud data
            const metaRef = doc(db, 'users', uid, '_meta', 'info');
            const metaSnap = await getDoc(metaRef);
            const hasCloudData = metaSnap.exists();

            if (hasCloudData) {
                // ─── PULL: Cloud → Local ───
                await pullFromFirestore(uid);
            } else {
                // ─── PUSH: Local → Cloud (first-time migration) ───
                await pushToFirestore(uid);
                await setDoc(metaRef, { migrated: true, createdAt: Date.now() });
            }

            // Run data migrations against the user-scoped localStorage keys
            // AFTER cloud pull (so cloud data is migrated) and BEFORE rehydrate
            // (so stores see the fully migrated data).
            runMigrations(uid);

            // Re-hydrate all stores so they pick up the cloud-pulled data
            _fireRehydrate();
            _setSyncStatus('synced');
        } finally {
            _syncing = false; // Unlock writes
        }

        // Flush any pending writes from a previous session
        await _flushPendingWrites();
    };

    if (hasLocalData) {
        // ─── OFFLINE-FIRST: Unblock auth gate immediately
        // App loads in 0ms using local cache. Cloud sync happens in background.
        bootSequence().catch(e => {
            _syncing = false;
            _setSyncStatus('error');
            console.warn('[Cloud sync] Background sync failed — working offline:', e.message);
            _scheduleRetrySync(uid);
        });
    } else {
        // ─── COLD START: Block until download finishes, but with a timeout
        // so the app doesn't hang forever if launched before network is ready.
        try {
            await Promise.race([
                bootSequence(),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Boot timeout — Firestore unreachable')), BOOT_TIMEOUT_MS)
                ),
            ]);
        } catch (e) {
            console.warn('[Cloud sync] Cold start failed — using local cache:', e.message);
            _syncing = false;
            _setSyncStatus('error');
            // Still fire rehydrate so stores initialize from whatever localStorage has
            _fireRehydrate();
            _scheduleRetrySync(uid);
        }
    }
}

/**
 * Schedule a background retry of cloud sync once the network comes online.
 * Uses the `online` event listener so we don't poll needlessly.
 */
function _scheduleRetrySync(uid) {
    const retry = async () => {
        console.log('[Cloud sync] Network available — retrying sync...');
        window.removeEventListener('online', retry);
        try {
            _syncing = true;
            const metaRef = doc(db, 'users', uid, '_meta', 'info');
            const metaSnap = await getDoc(metaRef);
            const hasCloudData = metaSnap.exists();

            if (hasCloudData) {
                await pullFromFirestore(uid);
            } else {
                await pushToFirestore(uid);
                await setDoc(metaRef, { migrated: true, createdAt: Date.now() });
            }

            runMigrations(uid);
            _fireRehydrate();
            _setSyncStatus('synced');
        } catch (e) {
            console.warn('[Cloud sync] Retry failed:', e.message);
            _setSyncStatus('error');
        } finally {
            _syncing = false;
        }

        // Flush pending writes
        await _flushPendingWrites();
    };

    if (navigator.onLine) {
        // Already online but Firestore timed out — retry after a short delay
        setTimeout(retry, 5000);
    } else {
        // Offline — wait for network
        window.addEventListener('online', retry);
    }
}

async function pullFromFirestore(uid) {
    const pullTimestamp = Date.now();

    // Pull all collections and single docs in parallel for faster login
    const collectionPulls = COLLECTION_KEYS.map(async (key) => {
        try {
            const colRef = collection(db, 'users', uid, key);
            const snapshot = await getDocs(colRef);
            if (!snapshot.empty) {
                let items = snapshot.docs.map(d => d.data());

                if (SORTABLE_COLLECTIONS.includes(key)) {
                    items = items
                        .map((item, index) => ({ item, index }))
                        .sort((a, b) => {
                            const itemA = a.item;
                            const itemB = b.item;

                            const hasA = itemA.sortOrder !== undefined && itemA.sortOrder !== null;
                            const hasB = itemB.sortOrder !== undefined && itemB.sortOrder !== null;
                            if (hasA && hasB) {
                                return itemA.sortOrder - itemB.sortOrder;
                            }
                            if (hasA) return -1;
                            if (hasB) return 1;

                            const createA = itemA.createdAt !== undefined && itemA.createdAt !== null;
                            const createB = itemB.createdAt !== undefined && itemB.createdAt !== null;
                            if (createA && createB) {
                                if (itemA.createdAt < itemB.createdAt) return -1;
                                if (itemA.createdAt > itemB.createdAt) return 1;
                                return 0;
                            }
                            if (createA) return -1;
                            if (createB) return 1;

                            return a.index - b.index;
                        })
                        .map(x => x.item);
                }

                localStorage.setItem(`${STORAGE_PREFIX}${uid}_${key}`, JSON.stringify(items));

                // Seed _lastSyncedState so delta sync knows what Firestore has
                const stateMap = new Map();
                items.forEach(item => {
                    if (item.id) stateMap.set(item.id, JSON.stringify(item));
                });
                _lastSyncedState.set(key, stateMap);
            }

            // Record pull timestamp for reconciliation (Task 4.5)
            _lastPulledAt.set(key, pullTimestamp);
        } catch (err) {
            console.warn(`[Cloud sync] Pull failed for ${key}:`, err.message);
        }
    });

    // Includes PER_DEVICE_SINGLE_DOC_KEYS — _singleDocId() resolves each to its
    // device-suffixed doc id while the LOCAL storage key it's written to stays the plain,
    // un-suffixed `${STORAGE_PREFIX}${uid}_${key}` every consumer already expects.
    const singleDocPulls = [...SINGLE_DOC_KEYS, ...PER_DEVICE_SINGLE_DOC_KEYS].map(async (key) => {
        try {
            const docRef = doc(db, 'users', uid, '_singles', _singleDocId(key));
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                const data = snap.data();
                localStorage.setItem(`${STORAGE_PREFIX}${uid}_${key}`, JSON.stringify(data.value));
            }
            // Record pull timestamp for reconciliation (Task 4.5)
            _lastPulledAt.set(key, pullTimestamp);
        } catch (err) {
            console.warn(`[Cloud sync] Pull failed for ${key}:`, err.message);
        }
    });

    await Promise.allSettled([...collectionPulls, ...singleDocPulls]);
}

async function pushToFirestore(uid) {
    // Push collections
    for (const key of COLLECTION_KEYS) {
        // Try UID-namespaced key first, then fall back to legacy unprefixed key
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${uid}_${key}`) || localStorage.getItem(STORAGE_PREFIX + key);
        if (raw) {
            try {
                const items = JSON.parse(raw);
                if (Array.isArray(items) && items.length > 0) {
                    await _syncToFirestoreInternal(key, items);
                    // Ensure the UID-namespaced key exists
                    localStorage.setItem(`${STORAGE_PREFIX}${uid}_${key}`, raw);
                }
            } catch (err) {
                console.warn(`[Cloud sync] Push failed for ${key}:`, err.message);
            }
        }
    }

    // Push single docs (PER_DEVICE_SINGLE_DOC_KEYS included — _syncToFirestoreInternal
    // already routes each key to its correct doc id via _singleDocId()).
    for (const key of [...SINGLE_DOC_KEYS, ...PER_DEVICE_SINGLE_DOC_KEYS]) {
        // Try UID-namespaced key first, then fall back to legacy unprefixed key
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${uid}_${key}`) || localStorage.getItem(STORAGE_PREFIX + key);
        if (raw) {
            try {
                const value = JSON.parse(raw);
                await _syncToFirestoreInternal(key, value);
                // Ensure the UID-namespaced key exists
                localStorage.setItem(`${STORAGE_PREFIX}${uid}_${key}`, raw);
            } catch (err) {
                console.warn(`[Cloud sync] Push failed for ${key}:`, err.message);
            }
        }
    }
}
